import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Dialog, InputBase, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PushPin, SearchOutlined } from '@mui/icons-material';
import { CHROME_OVERLAY_Z, getNavTokens, navFocusRing } from './navTokens';

export interface GoToEntry {
  label: string;
  /** Onde a página fica (ex.: "Produção"), mostrado à direita. */
  context: string;
  path: string;
  /** Aba fixada: vem primeiro e mostra o alfinete, como na barra de abas. */
  pinned?: boolean;
}

interface GoToDialogProps {
  open: boolean;
  entries: GoToEntry[];
  onClose: () => void;
  onNavigate: (path: string) => void;
}

const normalize = (value: string) =>
  value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const MAX_RESULTS = 8;

/** Busca "Ir para…" (Ctrl K): só lista páginas que o usuário pode abrir. */
export const GoToDialog: React.FC<GoToDialogProps> = ({ open, entries, onClose, onNavigate }) => {
  const t = getNavTokens(useTheme());
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const results = useMemo(() => {
    const q = normalize(query.trim());
    const matches = q
      ? entries.filter((entry) => normalize(`${entry.label} ${entry.context}`).includes(q))
      : entries;
    return matches.slice(0, MAX_RESULTS);
  }, [entries, query]);

  const choose = (entry: GoToEntry | undefined) => {
    if (!entry) return;
    onNavigate(entry.path);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((i) => Math.min(i + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(results[highlight]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disablePortal={false}
      TransitionProps={{ onExited: () => { setQuery(''); setHighlight(0); } }}
      sx={{ zIndex: CHROME_OVERLAY_Z + 1, '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      slotProps={{
        paper: {
          'aria-label': 'Ir para',
          sx: { mt: '12vh', width: 600, maxWidth: 'calc(100% - 32px)', borderRadius: '16px', backgroundImage: 'none', overflow: 'hidden' },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: 56, px: 2 }}>
        <SearchOutlined sx={{ color: t.muted }} />
        <InputBase
          autoFocus
          fullWidth
          value={query}
          onChange={(event) => { setQuery(event.target.value); setHighlight(0); }}
          onKeyDown={handleKeyDown}
          placeholder="Ir para…"
          inputProps={{
            'aria-label': 'Buscar página',
            role: 'combobox',
            'aria-expanded': true,
            'aria-controls': 'goto-results',
            'aria-autocomplete': 'list',
            // Foco fica no campo; o leitor de tela anuncia a opção destacada pelas setas.
            'aria-activedescendant': results[highlight] ? `goto-option-${highlight}` : undefined,
          }}
          sx={{ fontSize: '1rem' }}
        />
      </Box>
      <Box id="goto-results" role="listbox" sx={{ px: 1, pb: 1, display: 'flex', flexDirection: 'column', gap: '1px' }}>
        {results.length === 0 && (
          <Typography sx={{ px: 1.5, py: 2, fontSize: '0.875rem', color: t.muted }}>
            Nenhuma página encontrada.
          </Typography>
        )}
        {results.map((entry, index) => (
          <ButtonBase
            key={entry.path}
            id={`goto-option-${index}`}
            tabIndex={-1}
            role="option"
            aria-selected={index === highlight}
            onClick={() => choose(entry)}
            onMouseMove={() => setHighlight(index)}
            sx={{
              height: 40,
              borderRadius: '10px',
              px: 1.5,
              gap: 1.5,
              justifyContent: 'flex-start',
              fontSize: '0.875rem',
              color: t.ink,
              backgroundColor: index === highlight ? t.active : 'transparent',
              ...navFocusRing(t),
            }}
          >
            <Box component="span" sx={{ textAlign: 'left' }}>{entry.label}</Box>
            {entry.pinned && <PushPin aria-label="Fixada" sx={{ fontSize: 14, color: t.muted, ml: -0.75 }} />}
            <Box component="span" sx={{ flex: 1 }} />
            <Box component="span" sx={{ fontSize: '0.78rem', color: t.muted }}>{entry.context}</Box>
          </ButtonBase>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, px: 2.5, py: 1.25, fontSize: '0.75rem', color: t.muted, bgcolor: t.canvas }}>
        <span>↑ ↓ navegar</span>
        <span>Enter abrir</span>
        <span>Esc fechar</span>
      </Box>
    </Dialog>
  );
};
